/*
 * WaWiAuftragEinerVersandanweisungen.tsx
 *
 * Created by Dr. Maximillian Dornseif 2023-11-06 in huWaWi3 33.4.0
 * Copyright (c) 2023 HUDORA GmbH
 */

import { ICommandBarItemProps } from '@fluentui/react'
import { LadeChecker, getLink } from '@hudora/hd-react-components'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import { assertIsArray } from 'assertate-debug'
import * as React from 'react'
import toast from 'react-hot-toast'
import { AnyVariables, OperationResult, useMutation, useQuery } from 'urql'
import { navigate } from 'wouter/use-location'

import { HwAuftrag } from '../../gql/graphql'
import { useEditPanel3 } from '../../lib/hooks/useEditPanel3'
import { LinkRender } from '../../lib/hd-link'
import { VA_EDIT_MUTATION } from '../../Stammdaten/StammKundenAppQueries'
import { useHdDialog } from '../components/HdDialog'
import { VA_CREATE_MUTATION } from '../WaWiAppQueries'
import { VersandanweisungenCard } from './VersandanweisungenCard'
import { AUFTRAG_ALLE_VERSANDANWEISUNGEN_QUERY } from './WaWiAuftragEinerQuery'

// eslint-disable-next-line react-refresh/only-export-components
export function useVersandanweisungGui(auftrag: HwAuftrag) {
  const [{ data, fetching, error }] = useQuery({
    query: AUFTRAG_ALLE_VERSANDANWEISUNGEN_QUERY,
    variables: { id: auftrag.auftragsnr },
    pause: !auftrag.auftragsnr,
  })
  const [, createEntity] = useMutation(VA_CREATE_MUTATION)
  const {
    openDialog: openCreateDialog,
    renderDialog: renderCreateDialog,
    isDialogOpen: isCreateDialogOpen,
    dismissDialog: dismissCreateDialog,
  } = useHdDialog('Versandanweisungen', 'Auftragsspezifische Versandanweisung bearbeiten.')
  const versandanweisung = data?.entity?.versandanweisung
  const [, editPanel, openEditPanel] = useEditPanel3({
    schemaName: 'HwVersandanweisung',
    mutation: VA_EDIT_MUTATION,
    entity: versandanweisung,
  })
  const myOpen = React.useCallback(() => {
    versandanweisung ? openEditPanel(versandanweisung) : openCreateDialog()
  }, [versandanweisung, openEditPanel, openCreateDialog])

  const commandBarItem = {
    key: 'versandanweisungen',
    text: 'Versandanweisungen',
    ariaLabel: 'Versandanweisungen',
    iconOnly: false,
    iconProps: { iconName: 'DeliveryTruck' },
    disabled: isCreateDialogOpen || !(auftrag.status < '301'),
    onClick: myOpen,
  } as ICommandBarItemProps

  const neuAnlegen = () => {
    const newVa = {
      referenz: auftrag.auftragsnr,
      kundennr: auftrag.empfaengernr,
      ist_aktiv: true,
      avisieren_tage: undefined,
      avisieren_unter: undefined,
      hebebuehne: undefined,
      selbstabholer: undefined,
      info_lager: undefined,
      info_spediteur: undefined,
      versandart_unfrei: undefined,
      ccg_i: undefined,
    }
    assertIsArray(data.entity.versandanweisungen)
    for (const va in data.entity.versandanweisungen) {
      const anyVA = va as unknown as Record<string, any>
      newVa.avisieren_tage = anyVA.avisieren_tage
      newVa.avisieren_unter = anyVA.hebebuehne
      newVa.hebebuehne = anyVA.hebebuehne
      newVa.selbstabholer = anyVA.selbstabholer
      newVa.info_lager = anyVA.info_lager
      newVa.info_spediteur = anyVA.info_spediteur
      newVa.versandart_unfrei = anyVA?.versandart_unfrei
      newVa.ccg_i = anyVA.ccg_i
    }

    const prom = async () => {
      const r = await createEntity({ input: newVa, designator: `VA${auftrag.auftragsnr}` } as any)
      if (r.error) throw r.error
      return r
    }
    toast.promise(prom(), {
      loading: 'Erzeuge Auftrags-Versandanweisung',
      success: (r: OperationResult<any, AnyVariables>) => {
        if (r.error) return `Fehler: ${r.error?.message}`
        return (
          <React.Fragment>
            Auftrags-Versandanweisung {JSON.stringify(r?.data?.entity?.designator)} erzeugt.
          </React.Fragment>
        )
      },
      error: (e) => {
        console.error(e)
        return `Fehler: ${e}`
      },
    })
  }

  const versandanweisungenEditPanel = renderCreateDialog({
    renderBody: () => (
      <div>
        <LadeChecker loading={fetching} error={error}>
          {versandanweisung ? (
            <React.Fragment>
              Sie können die Versandanweisung <LinkRender value={versandanweisung.designator} /> jetzt
              bearbeiten.
            </React.Fragment>
          ) : (
            <div>
              Es gibt noch <em>keine</em> auftragsspezifische Versandanweisung.
            </div>
          )}
        </LadeChecker>
      </div>
    ),
    renderActions: () => (
      <div>
        <LadeChecker loading={fetching} error={error}>
          {versandanweisung ? (
            <Stack direction="row" spacing={2} justifyContent="space-between">
              <Button variant="outlined" onClick={dismissCreateDialog}>
                Abbrechen
              </Button>
              {/* <Button onClick={() => (submitFormRef?.current as any)?.click()}> */}
              <Button onClick={() => navigate(getLink(versandanweisung.designator))} variant="contained">
                zur Versandanweisung
              </Button>
            </Stack>
          ) : (
            <div>
              <Button onClick={neuAnlegen} variant="contained">
                Versandanweisung anlegen
              </Button>
            </div>
          )}
        </LadeChecker>
      </div>
    ),
  })

  return {
    commandBarItem,
    versandanweisungenShowPanel: (
      <>
        {editPanel}
        <VersandanweisungenCard anweisungen={data?.entity?.versandanweisungen} />
      </>
    ),
    versandanweisungenEditPanel,
  }
}
